import { collection, getDocs, doc, getDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { UserLoyaltyStatus } from '../context/AuthContext';

export interface CarnivalMigrationUserPreview {
  uid: string;
  email: string;
  displayName: string;
  currentStatus: number;
  legacyPoints?: number;
  proposedTier: 'Diamond' | 'Platinum' | 'Gold' | 'Red';
  proposedExpiry: 'lifetime' | '2028-05-31' | '2028-12-31';
  isAlreadyMigrated: boolean;
  existingGrandfatheredTier?: string;
  hasPlatinumBoost?: boolean;
}

export interface CarnivalMigrationPreview {
  totalUsersScanned: number;
  totalCarnivalMembers: number;
  alreadyMigratedCount: number;
  pendingMigrationCount: number;
  breakdown: {
    diamond: number;
    platinum: number;
    gold: number;
    red: number;
  };
  users: CarnivalMigrationUserPreview[];
}

export interface CarnivalMigrationResult {
  success: boolean;
  migratedCount: number;
  alreadyMigratedCount: number;
  errors: string[];
}

/**
 * Checks whether a cruise line name represents Carnival.
 */
export const isCarnivalCruiseLine = (cruiseLineName?: string): boolean => {
  if (!cruiseLineName) return false;
  const lower = cruiseLineName.toLowerCase().trim();
  return lower.includes('carnival');
};

/**
 * Determines the grandfathered status and expiration based on legacy VIFP days sailed.
 */
export const determineGrandfatheredStatus = (days: number): {
  tier: 'Diamond' | 'Platinum' | 'Gold' | 'Red';
  expiry: 'lifetime' | '2028-05-31' | '2028-12-31';
} => {
  if (days >= 200) {
    return { tier: 'Diamond', expiry: 'lifetime' };
  } else if (days >= 75) {
    return { tier: 'Platinum', expiry: '2028-05-31' };
  } else if (days >= 25) {
    return { tier: 'Gold', expiry: '2028-12-31' };
  } else {
    return { tier: 'Red', expiry: '2028-12-31' };
  }
};

/**
 * Scans all Firestore users and produces a dry-run preview of Carnival grandfathered migration.
 */
export const previewCarnivalMigration = async (): Promise<CarnivalMigrationPreview> => {
  const usersSnap = await getDocs(collection(db, 'users'));
  
  const preview: CarnivalMigrationPreview = {
    totalUsersScanned: usersSnap.size,
    totalCarnivalMembers: 0,
    alreadyMigratedCount: 0,
    pendingMigrationCount: 0,
    breakdown: {
      diamond: 0,
      platinum: 0,
      gold: 0,
      red: 0,
    },
    users: [],
  };

  usersSnap.forEach((docSnap) => {
    const data = docSnap.data();
    const loyaltyList: UserLoyaltyStatus[] = data.loyaltyStatus || [];
    
    const carnivalRecord = loyaltyList.find((l) => isCarnivalCruiseLine(l.cruiseLineName));
    if (!carnivalRecord) return;

    preview.totalCarnivalMembers++;

    const isAlreadyMigrated =
      carnivalRecord.legacyPoints !== undefined ||
      carnivalRecord.grandfatheredTier !== undefined;

    const currentStatus = Number(carnivalRecord.status) || 0;
    const legacyVal = carnivalRecord.legacyPoints !== undefined ? carnivalRecord.legacyPoints : currentStatus;
    const { tier, expiry } = determineGrandfatheredStatus(legacyVal);

    if (isAlreadyMigrated) {
      preview.alreadyMigratedCount++;
    } else {
      preview.pendingMigrationCount++;
    }

    // Count breakdown by proposed or existing grandfathered tier
    const effectiveTier = (carnivalRecord.grandfatheredTier as any) || tier;
    if (effectiveTier === 'Diamond') preview.breakdown.diamond++;
    else if (effectiveTier === 'Platinum') preview.breakdown.platinum++;
    else if (effectiveTier === 'Gold') preview.breakdown.gold++;
    else preview.breakdown.red++;

    const hasPlatinumBoost = effectiveTier === 'Platinum' ? currentStatus >= 10000 : undefined;

    preview.users.push({
      uid: docSnap.id,
      email: data.email || data.consentData?.email || 'Unknown',
      displayName: data.preferredDisplayName || data.displayName || 'Unnamed User',
      currentStatus,
      legacyPoints: carnivalRecord.legacyPoints,
      proposedTier: tier,
      proposedExpiry: expiry,
      isAlreadyMigrated,
      existingGrandfatheredTier: carnivalRecord.grandfatheredTier,
      hasPlatinumBoost,
    });
  });

  // Sort: pending migration first, then by currentStatus descending
  preview.users.sort((a, b) => {
    if (a.isAlreadyMigrated !== b.isAlreadyMigrated) {
      return a.isAlreadyMigrated ? 1 : -1;
    }
    return b.currentStatus - a.currentStatus;
  });

  return preview;
};

/**
 * Executes Phase 1 migration: preloads and saves legacyPoints, grandfatheredTier,
 * grandfatheredExpiry, and cycleEndDate for all unmigrated Carnival records in Firestore.
 * 
 * NOTE: Preserves the user's active `status` as-is during Phase 1 so that the live application
 * is not disrupted before Phase 3 cutover!
 */
export const executeCarnivalMigration = async (): Promise<CarnivalMigrationResult> => {
  const usersSnap = await getDocs(collection(db, 'users'));
  const mode = await getCarnivalProgramMode();
  const isRewardsActive = mode.isCarnivalRewardsActive;
  const errors: string[] = [];
  let migratedCount = 0;
  let alreadyMigratedCount = 0;

  const usersToUpdate: {
    uid: string;
    updatedLoyaltyStatus: UserLoyaltyStatus[];
  }[] = [];

  usersSnap.forEach((docSnap) => {
    const data = docSnap.data();
    const loyaltyList: UserLoyaltyStatus[] = data.loyaltyStatus || [];

    let needsUpdate = false;
    const updatedList = loyaltyList.map((record) => {
      if (!isCarnivalCruiseLine(record.cruiseLineName)) {
        return record;
      }

      // Check if already migrated
      if (record.legacyPoints !== undefined || record.grandfatheredTier !== undefined) {
        alreadyMigratedCount++;
        return record;
      }

      needsUpdate = true;
      const currentDays = Number(record.status) || 0;
      const { tier, expiry } = determineGrandfatheredStatus(currentDays);

      let activeStatus = currentDays;
      if (isRewardsActive) {
        // If Carnival Rewards is active in Firestore, Platinum gets 10k bonus; other tiers start at 0
        activeStatus = tier === 'Platinum' ? 10000 : 0;
      }

      const migratedRecord: UserLoyaltyStatus = {
        ...record,
        legacyPoints: currentDays,
        grandfatheredTier: tier,
        grandfatheredExpiry: expiry,
        cycleEndDate: '2028-12-31',
        status: activeStatus,
      };

      return migratedRecord;
    });

    if (needsUpdate) {
      usersToUpdate.push({
        uid: docSnap.id,
        updatedLoyaltyStatus: updatedList,
      });
    }
  });

  // Execute in batches of up to 400 (Firestore maximum per batch is 500)
  const BATCH_SIZE = 400;
  for (let i = 0; i < usersToUpdate.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = usersToUpdate.slice(i, i + BATCH_SIZE);

    chunk.forEach(({ uid, updatedLoyaltyStatus }) => {
      const userDocRef = doc(db, 'users', uid);
      batch.update(userDocRef, { loyaltyStatus: updatedLoyaltyStatus });
    });

    try {
      await batch.commit();
      migratedCount += chunk.length;
    } catch (err: any) {
      console.error('Error committing migration batch:', err);
      errors.push(`Failed batch ${Math.floor(i / BATCH_SIZE) + 1}: ${err?.message || err}`);
    }
  }

  return {
    success: errors.length === 0,
    migratedCount,
    alreadyMigratedCount,
    errors,
  };
};

export const CARNIVAL_REWARDS_METADATA_CONFIG = {
  loyaltyProgramName: 'Carnival Rewards',
  loyaltyUnit: 'stars',
  loyaltyCalculationRules: {
    method: 'manual',
    forceManualInput: true,
    supportsGrandfathering: true,
    cycleDurationYears: 2,
    currentCycleEnd: '2028-12-31',
  },
  loyaltyTiers: [
    { name: 'Red', min_value: 0, max_value: 9999 },
    { name: 'Gold', min_value: 10000, max_value: 49999 },
    { name: 'Platinum', min_value: 50000, max_value: 99999 },
    { name: 'Diamond', min_value: 100000, max_value: 999999999 },
  ],
};

export const LEGACY_VIFP_METADATA_CONFIG = {
  loyaltyProgramName: 'VIFP Club',
  loyaltyUnit: 'points',
  loyaltyCalculationRules: {
    method: 'days',
    pointsPerDay: 1,
    forceManualInput: false,
  },
  loyaltyTiers: [
    { name: 'Blue', min_value: 0, max_value: 0 },
    { name: 'Red', min_value: 1, max_value: 24 },
    { name: 'Gold', min_value: 25, max_value: 74 },
    { name: 'Platinum', min_value: 75, max_value: 199 },
    { name: 'Diamond', min_value: 200, max_value: 99999 },
  ],
};

export interface CarnivalProgramModeStatus {
  isCarnivalRewardsActive: boolean;
  programName: string;
  loyaltyUnit: string;
  method: string;
  forceManualInput: boolean;
}

/**
 * Checks Firestore cruiseMetadata/carnival to see which program is currently active.
 */
export const getCarnivalProgramMode = async (): Promise<CarnivalProgramModeStatus> => {
  try {
    const metaDocRef = doc(db, 'cruiseMetadata', 'carnival');
    const snap = await getDoc(metaDocRef);
    if (!snap.exists()) {
      return {
        isCarnivalRewardsActive: false,
        programName: 'VIFP Club',
        loyaltyUnit: 'points',
        method: 'days',
        forceManualInput: false,
      };
    }
    const data = snap.data();
    const isRewards = data.loyaltyProgramName === 'Carnival Rewards' || data.loyaltyCalculationRules?.method === 'manual';
    return {
      isCarnivalRewardsActive: isRewards,
      programName: data.loyaltyProgramName || 'VIFP Club',
      loyaltyUnit: data.loyaltyUnit || 'points',
      method: data.loyaltyCalculationRules?.method || 'days',
      forceManualInput: Boolean(data.loyaltyCalculationRules?.forceManualInput),
    };
  } catch (err) {
    console.error('Error fetching Carnival program mode:', err);
    return {
      isCarnivalRewardsActive: false,
      programName: 'VIFP Club',
      loyaltyUnit: 'points',
      method: 'days',
      forceManualInput: false,
    };
  }
};

/**
 * Phase 3 Cutover: Enables Carnival Rewards in Firestore.
 * Updates cruiseMetadata/carnival to manual/stars/2-year cycle,
 * and initializes users' active status to 0 Stars while preserving legacy VIFP days.
 */
export const enableCarnivalRewardsMode = async (): Promise<{ success: boolean; updatedUsersCount: number; errors: string[] }> => {
  const errors: string[] = [];
  let updatedUsersCount = 0;

  try {
    // 1. Update cruiseMetadata/carnival document
    const metaDocRef = doc(db, 'cruiseMetadata', 'carnival');
    await updateDoc(metaDocRef, CARNIVAL_REWARDS_METADATA_CONFIG);
  } catch (err: any) {
    console.error('Error updating cruiseMetadata for Carnival Rewards:', err);
    return { success: false, updatedUsersCount: 0, errors: [err?.message || 'Failed to update cruiseMetadata/carnival'] };
  }

  // 2. Update users' Carnival active status to 0 Stars while securing legacyPoints
  try {
    const usersSnap = await getDocs(collection(db, 'users'));
    const usersToUpdate: { uid: string; updatedLoyaltyStatus: UserLoyaltyStatus[] }[] = [];

    usersSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const loyaltyList: UserLoyaltyStatus[] = data.loyaltyStatus || [];

      let needsUpdate = false;
      const updatedList = loyaltyList.map((record) => {
        if (!isCarnivalCruiseLine(record.cruiseLineName)) return record;

        needsUpdate = true;
        const currentVal = Number(record.status) || 0;
        const legacyVal = record.legacyPoints !== undefined ? record.legacyPoints : currentVal;
        const { tier, expiry } = determineGrandfatheredStatus(legacyVal);
        const resolvedTier = record.grandfatheredTier || tier;

        // PLATINUM 10k BIENNIAL BOOST:
        // Grandfathered Platinum members start each 2-year earning cycle with a 10,000 Star bonus.
        // Therefore, their active balance starts at 10,000 Stars (or higher if already earned in this cycle).
        let newStatus = 0;
        if (resolvedTier === 'Platinum') {
          newStatus = Math.max(10000, currentVal >= 10000 ? currentVal : 10000);
        }

        return {
          ...record,
          legacyPoints: legacyVal,
          grandfatheredTier: resolvedTier,
          grandfatheredExpiry: record.grandfatheredExpiry || expiry,
          cycleEndDate: record.cycleEndDate || '2028-12-31',
          status: newStatus,
        };
      });

      if (needsUpdate) {
        usersToUpdate.push({ uid: docSnap.id, updatedLoyaltyStatus: updatedList });
      }
    });

    const BATCH_SIZE = 400;
    for (let i = 0; i < usersToUpdate.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = usersToUpdate.slice(i, i + BATCH_SIZE);
      chunk.forEach(({ uid, updatedLoyaltyStatus }) => {
        batch.update(doc(db, 'users', uid), { loyaltyStatus: updatedLoyaltyStatus });
      });
      await batch.commit();
      updatedUsersCount += chunk.length;
    }
  } catch (err: any) {
    console.error('Error updating users for Carnival Rewards:', err);
    errors.push(err?.message || 'Failed to update users');
  }

  return {
    success: errors.length === 0,
    updatedUsersCount,
    errors,
  };
};

/**
 * Applies the 10,000 Star biennial bonus to all Grandfathered Platinum Carnival accounts
 * whose active status is currently below 10,000.
 */
export const applyPlatinumBoostToUsers = async (): Promise<{ success: boolean; updatedCount: number; errors: string[] }> => {
  const errors: string[] = [];
  let updatedCount = 0;

  try {
    const usersSnap = await getDocs(collection(db, 'users'));
    const usersToUpdate: { uid: string; updatedLoyaltyStatus: UserLoyaltyStatus[] }[] = [];

    usersSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const loyaltyList: UserLoyaltyStatus[] = data.loyaltyStatus || [];

      let needsUpdate = false;
      const updatedList = loyaltyList.map((record) => {
        if (!isCarnivalCruiseLine(record.cruiseLineName)) return record;

        const currentVal = Number(record.status) || 0;
        const legacyVal = record.legacyPoints !== undefined ? record.legacyPoints : currentVal;
        const { tier, expiry } = determineGrandfatheredStatus(legacyVal);
        const resolvedTier = record.grandfatheredTier || tier;

        if (resolvedTier === 'Platinum' && currentVal < 10000) {
          needsUpdate = true;
          return {
            ...record,
            legacyPoints: legacyVal,
            grandfatheredTier: 'Platinum',
            grandfatheredExpiry: record.grandfatheredExpiry || expiry,
            cycleEndDate: record.cycleEndDate || '2028-12-31',
            status: 10000, // Enforce 10,000 Star bonus
          };
        }
        return record;
      });

      if (needsUpdate) {
        usersToUpdate.push({ uid: docSnap.id, updatedLoyaltyStatus: updatedList });
      }
    });

    const BATCH_SIZE = 400;
    for (let i = 0; i < usersToUpdate.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = usersToUpdate.slice(i, i + BATCH_SIZE);
      chunk.forEach(({ uid, updatedLoyaltyStatus }) => {
        batch.update(doc(db, 'users', uid), { loyaltyStatus: updatedLoyaltyStatus });
      });
      await batch.commit();
      updatedCount += chunk.length;
    }
  } catch (err: any) {
    console.error('Error applying Platinum boost:', err);
    errors.push(err?.message || 'Failed to apply Platinum boost');
  }

  return {
    success: errors.length === 0,
    updatedCount,
    errors,
  };
};

/**
 * Reverts Carnival back to legacy VIFP Club mode in Firestore.
 * Restores cruiseMetadata/carnival to days/points/VIFP Club,
 * and restores users' active status back to their legacyPoints (sailing days).
 */
export const revertToLegacyVIFPMode = async (): Promise<{ success: boolean; restoredUsersCount: number; errors: string[] }> => {
  const errors: string[] = [];
  let restoredUsersCount = 0;

  try {
    // 1. Revert cruiseMetadata/carnival document
    const metaDocRef = doc(db, 'cruiseMetadata', 'carnival');
    await updateDoc(metaDocRef, LEGACY_VIFP_METADATA_CONFIG);
  } catch (err: any) {
    console.error('Error reverting cruiseMetadata to legacy VIFP:', err);
    return { success: false, restoredUsersCount: 0, errors: [err?.message || 'Failed to revert cruiseMetadata/carnival'] };
  }

  // 2. Restore users' active status back to their legacyPoints (sailing days)
  try {
    const usersSnap = await getDocs(collection(db, 'users'));
    const usersToUpdate: { uid: string; updatedLoyaltyStatus: UserLoyaltyStatus[] }[] = [];

    usersSnap.forEach((docSnap) => {
      const data = docSnap.data();
      const loyaltyList: UserLoyaltyStatus[] = data.loyaltyStatus || [];

      let needsUpdate = false;
      const updatedList = loyaltyList.map((record) => {
        if (!isCarnivalCruiseLine(record.cruiseLineName)) return record;

        needsUpdate = true;
        const restoredStatus = record.legacyPoints !== undefined ? record.legacyPoints : (Number(record.status) || 0);

        return {
          ...record,
          status: restoredStatus, // Restore exact historical sailing days
          // Note: legacyPoints, grandfatheredTier, etc. remain saved safely in the background
        };
      });

      if (needsUpdate) {
        usersToUpdate.push({ uid: docSnap.id, updatedLoyaltyStatus: updatedList });
      }
    });

    const BATCH_SIZE = 400;
    for (let i = 0; i < usersToUpdate.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = usersToUpdate.slice(i, i + BATCH_SIZE);
      chunk.forEach(({ uid, updatedLoyaltyStatus }) => {
        batch.update(doc(db, 'users', uid), { loyaltyStatus: updatedLoyaltyStatus });
      });
      await batch.commit();
      restoredUsersCount += chunk.length;
    }
  } catch (err: any) {
    console.error('Error restoring users to legacy VIFP:', err);
    errors.push(err?.message || 'Failed to restore users');
  }

  return {
    success: errors.length === 0,
    restoredUsersCount,
    errors,
  };
};
